/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { DataItem, Timeline, TimelineOptions } from 'vis-timeline/standalone';

import { DataGroupExtended } from '../types';
import { useEffect, useRef } from 'react';

type visTimelineType = {
  containerRef: React.RefObject<HTMLDivElement>;
  items: DataItem[];
  groups: DataGroupExtended[];
  timelineOptions: TimelineOptions;
  setSelectedLogId: (url: string | undefined) => void;
};

export const useVisTimeline = ({ containerRef, items, groups, timelineOptions, setSelectedLogId }: visTimelineType) => {
  const timelineRef = useRef<Timeline | null>(null);
  // Remember the last user‑defined window
  const lastWindow = useRef<{ start: Date; end: Date } | null>(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    timelineRef.current = new Timeline(containerRef.current, [], timelineOptions);
    const RangeHandler = ({ start, end, byUser }: { start: Date; end: Date; byUser: boolean }) => {
      if (byUser) {
        lastWindow.current = { start, end };
      }
    };

    const handleItemSelect = ({ items }: { items: string[] }) => {
      const stepId = items[0];
      setSelectedLogId(stepId);
    };

    timelineRef.current.on('rangechange', RangeHandler);
    timelineRef.current.on('select', handleItemSelect);
    return () => {
      if (timelineRef.current) {
        timelineRef.current.off('rangechange', RangeHandler);
        timelineRef.current.off('select', handleItemSelect);
        timelineRef.current.destroy();
        timelineRef.current = null;
      }
    };
  }, [containerRef, timelineOptions, setSelectedLogId]);

  useEffect(() => {
    if (timelineRef.current) {
      timelineRef.current.setGroups(groups);
      timelineRef.current.setItems(items);
      if (lastWindow.current) {
        timelineRef.current.setWindow(lastWindow.current.start, lastWindow.current.end);
      } else {
        timelineRef.current.fit();
      }
    }
  }, [items, groups, timelineOptions]);

  return { timelineRef };
};
